import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { CommonService } from '@shared/services/common.service';
import { environment } from '@environments/environment';
import { buildMapEmbedPageUrl } from '@utils/map-embed-url.util';

export interface EmbedMapInteractionPayload {
  lat?: number;
  long?: number;
  radius?: number;
  [key: string]: unknown;
}

@Component({
  selector: 'app-form-map-iframe',
  templateUrl: './form-map-iframe.component.html',
  styleUrls: ['./form-map-iframe.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormMapIframeComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() url: string | null | undefined;
  @Input() embedMapMode = false;
  @Input() embedLocation: string | undefined;
  @Input() iframeId = 'map-iframe';
  @Input() heightPx = 450;
  @Input() showRadius: boolean = true;

  @Output() embedMapInteraction = new EventEmitter<EmbedMapInteractionPayload>();

  trustedSrc: SafeResourceUrl | null = null;

  private messageListener?: (event: MessageEvent) => void;

  constructor(
    private readonly sanitizer: DomSanitizer,
    private readonly cs: CommonService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['url'] || changes['embedLocation'] || changes['embedMapMode']) {
      this.rebuildTrustedSrc();
    }
    if (changes['embedMapMode']) {
      this.syncMessageListener();
    }
  }

  ngAfterViewInit(): void {
    if (this.embedMapMode && this.cs.isBrowser && !this.messageListener) {
      this.syncMessageListener();
    }
  }

  ngOnDestroy(): void {
    this.detachMessageListener();
  }

  private syncMessageListener(): void {
    this.detachMessageListener();
    if (!this.embedMapMode || !this.cs.isBrowser) {
      return;
    }
    this.messageListener = (event: MessageEvent) => {
      if (event.origin + '/' === environment.EMBED_BASE_URL) {
        this.embedMapInteraction.emit(event.data as EmbedMapInteractionPayload);
        this.cdr.markForCheck();
      }
    };
    window.addEventListener('message', this.messageListener);
  }

  private detachMessageListener(): void {
    if (this.messageListener && this.cs.isBrowser) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = undefined;
    }
  }

  private rebuildTrustedSrc(): void {
    if (!this.cs.isBrowser) {
      this.trustedSrc = null;
      return;
    }
    if (this.embedMapMode) {
      const built = buildMapEmbedPageUrl(this.embedLocation, this.showRadius, this.cs);
      this.trustedSrc = built
        ? this.sanitizer.bypassSecurityTrustResourceUrl(built)
        : null;
      return;
    }
    const trimmed = this.url != null ? String(this.url).trim() : '';
    this.trustedSrc = trimmed
      ? this.sanitizer.bypassSecurityTrustResourceUrl(trimmed)
      : null;
  }
}
